'use client';

import { usePortalContext } from '@cloai/uikit';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { JSX } from 'react';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import AllToolsIcon from '@/public/icons/all_tools.svg';
import PersonIcon from '@/public/icons/person.svg';
import Feed from '@/public/icons/to_feed.svg';
import HomeIcon from '@/public/icons/to_home.svg';
import { useGTMContext, useProfileContext } from '@/src/providers';

const styles = {
  footer:
    'fixed z-50 inset-x-0 m-auto px-5 bottom-0 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] bg-fill-footer backdrop-blur-2xl border border-footer flex justify-center',
  wrapper:
    'sm:min-w-[458px] sm:max-w-[458px] w-full m-auto flex items-center justify-center gap-4',
  button: 'w-14 shrink-0 flex flex-col items-center justify-center opacity-50',
  buttonActive: 'opacity-100',
  icon: 'w-4 h-4 mb-1',
  text: 'text-xs whitespace-nowrap',
};

const FooterButton = ({
  href,
  text,
  icon,
  onClick,
  isActive,
  isHidden = false,
}: {
  href?: string;
  text: string;
  icon: JSX.Element;
  isActive: boolean;
  isHidden?: boolean;
  onClick?: () => void;
}) => {
  if (isHidden) {
    return null;
  }
  if (href) {
    return (
      <Link
        key={text}
        href={href}
        prefetch={true}
        className={twMerge(styles.button, isActive && styles.buttonActive)}
        onClick={onClick}
      >
        {icon}
        <span className={styles.text}>{text}</span>
      </Link>
    );
  }

  return (
    <button
      key={text}
      type="button"
      onClick={onClick}
      className={twMerge(styles.button, isActive && styles.buttonActive)}
    >
      {icon}
      <span className={styles.text}>{text}</span>
    </button>
  );
};

export const Footer = () => {
  const t = useTranslations('MenuPage');
  const sendGTM = useGTMContext();
  const { openPortal, closePortal, currentPortal } = usePortalContext();
  const profile = useProfileContext();
  const pathname = usePathname();
  // const [loading, setLoading] = useState(false);

  const handleHomeClick = () => {
    sendGTM({
      event: 'Footer_menu_home',
    });

    if (pathname !== '/') {
      // setLoading(true);
    }
    closePortal();
  };

  const handleFeedClick = () => {
    sendGTM({
      event: 'Footer_menu_feed',
    });

    if (!pathname.includes('/feed')) {
      // setLoading(true);
    }
    closePortal();
  };

  const handleProfileClick = () => {
    sendGTM({
      event: 'Footer_menu_profile',
    });

    if (pathname !== '/profile') {
      // setLoading(true);
    }
    closePortal();
  };

  const handleAllToolsClick = () => {
    sendGTM({
      event: 'Footer_menu_tools',
    });

    closePortal();
    openPortal(PORTALS.AllTools);
  };

  const links = [
    {
      href: '/#trending',
      text: t('homeMenu'),
      icon: <HomeIcon className={styles.icon} />,
      onClick: handleHomeClick,
      isActive: currentPortal !== PORTALS.AllTools && pathname === '/',
    },
    {
      href: '/feed/forYou',
      text: t('feed'),
      icon: <Feed className={styles.icon} />,
      onClick: handleFeedClick,
      isActive:
        currentPortal !== PORTALS.AllTools && pathname.includes('/feed'),
    },
    {
      text: t('allToolsMenu'),
      icon: <AllToolsIcon className={styles.icon} />,
      onClick: handleAllToolsClick,
      isActive: currentPortal === PORTALS.AllTools,
    },
    {
      href: '/profile',
      text: t('profileMenu'),
      icon: <PersonIcon className={styles.icon} />,
      onClick: handleProfileClick,
      isActive: currentPortal !== PORTALS.AllTools && pathname === '/profile',
    },
  ];

  // useEffect(() => {
  //   setLoading(false);
  // }, [pathname]);

  if (profile?.isAcitveStartFunnel) {
    return;
  }

  return (
    <>
      {/* {loading && <AnimatedLogoFallback />} */}

      <footer className={styles.footer}>
        <div className={styles.wrapper}>
          {links.map((item) => (
            <FooterButton {...item} key={item.text} />
          ))}
        </div>
      </footer>
    </>
  );
};
